import { useQuery } from "@tanstack/react-query";
import { Siren } from "lucide-react";
import { api } from "@/lib/api";
import { Pill } from "@/components/Pill";
import { PendingBadge } from "@/components/Brand";
import { ALARM_LEVELS, fmtDate } from "@/lib/constants";

function CounterGroup({ title, data, kind, testId }) {
  return (
    <div className="rounded-xl border-2 border-black bg-white p-4" data-testid={testId}>
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">{title}</p>
      <ul className="mt-3 space-y-2">
        {Object.entries(data || {}).map(([k, v]) => (
          <li key={k} className="flex items-center justify-between gap-2">
            <Pill kind={kind} value={k} />
            <span className="font-display text-xl font-extrabold" data-testid={`${testId}-${k}`}>{v}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Escalations({ stats, onOpen }) {
  const { data, isLoading } = useQuery({ queryKey: ["escalations"], queryFn: () => api.get("/admin/escalations").then((r) => r.data), refetchInterval: 60000 });
  const tickets = data?.tickets || [];

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <CounterGroup title="By status" data={stats?.by_status} kind="status" testId="counter-status" />
        <CounterGroup title="By priority" data={stats?.by_priority} kind="priority" testId="counter-priority" />
        <CounterGroup title="By escalation level" data={stats?.by_escalation} kind="level" testId="counter-escalation" />
      </div>

      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-2xl font-bold">Escalated tickets <span className="text-zinc-500">(L2 · L3 · Out-of-SLA, worst first)</span></h2>
          <PendingBadge label="Admin alerts: email live · WhatsApp awaiting keys" testId="escalation-notify-pending-badge" />
        </div>
        {isLoading ? (
          <p className="text-zinc-600">Loading…</p>
        ) : tickets.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-zinc-400 bg-white py-12 text-center" data-testid="escalations-empty">
            <p className="font-display text-xl font-bold">Nothing escalated</p>
            <p className="text-sm text-zinc-600">All unresolved tickets are within L1 or on time.</p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="escalations-list">
            {tickets.map((t) => {
              const alarm = ALARM_LEVELS.includes(t.escalation_level);
              return (
                <li
                  key={t.id}
                  onClick={() => onOpen(t)}
                  className={`flex cursor-pointer flex-col gap-3 rounded-xl border-2 p-4 transition-colors sm:flex-row sm:items-center sm:justify-between ${alarm ? "border-[#DC2626] bg-[#FEF2F2] hover:bg-[#FEE2E2]" : "border-black bg-white hover:bg-[#FEF9C3]"}`}
                  data-testid={`escalation-row-${t.ticket_id}`}
                >
                  <div className="flex items-start gap-3">
                    {alarm && (
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#DC2626] text-white alarm-pulse" data-testid={`priority-alarm-${t.ticket_id}`}>
                        <Siren className="h-5 w-5" />
                      </span>
                    )}
                    <div>
                      <p className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-lg font-bold">{t.ticket_id}</span>
                        <Pill kind="level" value={t.escalation_level} />
                        <Pill kind="priority" value={t.priority} />
                        <Pill kind="status" value={t.status} />
                      </p>
                      <p className="mt-1 font-semibold">{t.name} · <span className="text-zinc-600">{t.product}</span></p>
                      <p className="text-sm text-zinc-700">{t.query}</p>
                    </div>
                  </div>
                  <div className="text-sm sm:text-right">
                    <p className="font-display text-2xl font-extrabold">{t.elapsed_working_hours}h</p>
                    <p className="text-zinc-600">working hours · created {fmtDate(t.created_at)}</p>
                    <p className="text-zinc-600">{t.assigned_to ? `With ${t.assigned_to}` : "Unassigned"}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
